/**
 * 事件参数选择面板（AB 实验事件筛选用）
 * 一级为参数名，二级为参数值或 numeric 类型的操作符+数值；numeric 且无 children 时右侧渲染 NumberOperatorValue
 */
import React, { useCallback, useMemo } from 'react';
import { useIntl, getLocale } from 'umi';
import classNames from 'classnames';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import NumberOperatorValue from '@/pages/datalab/components/NumberOperatorValue';
import { EventFilterNumberOptions, EventFilterTextOptions } from '@/pages/datalab/components/SortableDrag/configration';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { LANGUAGE_EN } from '@home/_config/config';
import type { RegionSelectValueType } from '@home/components/NewRegionSelect/type';
import type { IEventParameterPanelProps, IEventParameterPanelOptionItem } from '../../types';
import { getName } from '@home/utils/common';
import './index.scss';
import {
  buildEventParameterDisplayParts,
  formatNumericValueStr,
  NUMERIC_CHILD_SUFFIX,
} from '../EventPanel/utils';

/** 根据操作符与数值生成中英文展示文案 */
function buildNumericDisplayText(
  operator: string,
  operatorValue: string[],
  isEn: boolean,
  type: string
): { text: string; textEn: string; textCN: string; } {
  const isNumeric = type === 'numeric';
  const options = isNumeric ? EventFilterNumberOptions : EventFilterTextOptions;
  const findOperator = options?.find((opt) => opt.value === operator);
  const isBetween = operator === 'between';
  const operatorTextEn = findOperator?.text_en ?? '';
  const operatorTextCn = findOperator?.text ?? '';
  const valueStr = formatNumericValueStr(operator, operatorValue ?? []);
  const textEn = valueStr ? `${isBetween ? '' : operatorTextEn}${valueStr}` : operatorTextEn;
  const textCN = valueStr ? `${isBetween ? '' : operatorTextCn}${valueStr}` : operatorTextCn;
  return { text: getName(isEn, textEn, textCN), textEn, textCN };
}

/** 将 child 转为 RegionSelect 所需结构 */
function mapChildToOption(child: IEventParameterPanelOptionItem) {
  return {
    value: child.value,
    text: child.text,
    textEn: child.textEn ?? child.text,
    textCN: child.textCN ?? child.text,
  };
}

const EventParameterPanel: React.FC<IEventParameterPanelProps> = (props) => {
  const { value, onChange, options = [], locale, language, placeholder, ...rest } = props;
  const { getLocalText } = useLocal('filter');
  const { formatMessage } = useIntl();
  const localeStr = getLocale();
  const isEn = localeStr === LANGUAGE_EN;

  /** 转为 NewRegionSelect 的 options：numeric 且无 children 时注入默认二级项（文案用一级标题） */
  const regionSelectOptions = useMemo(() => {
    return options.map((item: IEventParameterPanelOptionItem) => {
      const isNumericEmpty =
        (item.type === 'numeric' || item.type === 'text') && (!item.children || item.children.length === 0);
      const numericVal = value?.numeric?.[item.value];
      const hasNumericValue = Boolean(numericVal?.operator || numericVal?.value?.length);
      const children = isNumericEmpty ?
        [{
          value: `${item.value}${NUMERIC_CHILD_SUFFIX}`,
          ...(hasNumericValue
            ? buildNumericDisplayText(
              numericVal?.operator ?? '',
              numericVal?.value ?? [],
              isEn,
              item.type ?? ''
            )
            : {
              text: item.text,
              textEn: item.textEn ?? item.text,
              textCN: item.textCN ?? item.text,
            }),
        }] : item.children?.map(mapChildToOption);

      return {
        value: item.value,
        text: item.text,
        textEn: item.textEn ?? item.text,
        textCN: item.textCN ?? item.text,
        type: item.type,
        children,
      };
    });
  }, [options, value, isEn]);

  /** 一级选项的 value 列表，供 NewRegionSelect realRegionsList 使用 */
  const realRegionsList = useMemo(() => {
    return options.map((item: IEventParameterPanelOptionItem) => item.value);
  }, [options]);

  /**
   * 级联选择变化：同步 type/regions/markets；若一级选中的是 numeric 类型则为其设置 numeric 初始值
   */
  const handleRegionChange = useCallback(
    (next: RegionSelectValueType | null) => {
      if (!next) {
        onChange(null);
        return;
      }
      const markets = next?.markets || [];
      const nextNumeric = { ...(value?.numeric || {}) };
      markets?.forEach((market: string) => {
        if (typeof market !== 'string' || !market.endsWith(NUMERIC_CHILD_SUFFIX)) return;
        const paramKey = market.slice(0, market.length - NUMERIC_CHILD_SUFFIX.length);
        if (!paramKey) return;
        const opt = options?.find((o: IEventParameterPanelOptionItem) => o.value === paramKey);
        if (opt?.type !== 'numeric' && opt?.type !== 'text') return;
        if (nextNumeric[paramKey] === undefined || nextNumeric[paramKey] === null) {
          nextNumeric[paramKey] = { operator: '', value: [] };
        }
      });
      onChange({
        ...value,
        type: next.type,
        regions: next?.regions || [],
        markets,
        numeric: nextNumeric,
      });
    },
    [value, onChange, options]
  );

  /**
   * 右侧自定义内容：当一级选中的是 numeric 类型时，渲染操作符+数值组件
   * 非 numeric 时返回 null，走默认的 checkbox 列表
   */
  const renderRightContent = useCallback(
    (
      selectedRegionItem: { value?: string; type?: string; textEn?: string; textCN?: string; },
      regionValue: RegionSelectValueType
    ) => {
      if (selectedRegionItem?.type !== 'numeric' && selectedRegionItem?.type !== 'text') return null;
      const paramKey = selectedRegionItem.value ?? '';
      const numericVal = value.numeric?.[paramKey];
      const operator = numericVal?.operator ?? '';
      const numericValue = numericVal?.value ?? [];
      const name = getName(isEn, selectedRegionItem?.textEn, selectedRegionItem?.textCN);

      /** 操作符或数值变化：同步更新 numeric，并确保左侧一级级联选项及对应二级项被勾选 */
      const handleNumericChange = (op: string, val: string[]): void => {
        const normalizedVal = val.every((v) => !String(v ?? '').trim()) ? [] : val;
        const regions = regionValue?.regions ?? [];
        const markets = regionValue?.markets ?? [];
        const numericChildValue = `${paramKey}${NUMERIC_CHILD_SUFFIX}`;
        const nextRegions = regions.includes(paramKey) ? regions : [...regions, paramKey];
        const nextMarkets = markets.includes(numericChildValue)
          ? markets
          : [...markets, numericChildValue];
        onChange({
          ...value,
          type: regionValue.type,
          regions: nextRegions,
          markets: nextMarkets,
          numeric: {
            ...(value?.numeric || {}),
            [paramKey]: { operator: op, value: normalizedVal },
          },
        });
      };
      const operatorOptions = selectedRegionItem?.type === 'numeric' ? EventFilterNumberOptions : EventFilterTextOptions;
      const valuePlaceholder = selectedRegionItem?.type === 'numeric' ? formatMessage({ id: 'dbl_filter_number' }) : formatMessage({ id: 'dbl_filter_value' });
      return (
        <>
          <div className='dbl-number-operator-title'>{name}</div>
          <NumberOperatorValue
            className={classNames({
              'dbl-number-operator-text': selectedRegionItem?.type === 'text',
            })}
            operator={operator}
            value={numericValue}
            onChange={handleNumericChange}
            operatorOptions={operatorOptions}
            valuePlaceholder={valuePlaceholder}
            operatorPlaceholder={formatMessage({ id: 'dbl_ab_experiment_event_occur_operator_placeholder' })}
            isEn={isEn}
            type={selectedRegionItem?.type === 'text' ? 'text' : 'number'}
          />
        </>
      );
    },
    [value, onChange, formatMessage, isEn]
  );

  /** 根据当前 value 与 options 生成参数摘要文案（与 EventPanel 共用 buildEventParameterDisplayParts） */
  const button = useMemo(() => {
    const paramParts = buildEventParameterDisplayParts({
      eventParam: value,
      eventParameterOptions: options,
      isEn,
    });
    return paramParts.length > 0 ? paramParts.join(', ') : '';
  }, [value, options, isEn, placeholder]);

  return (
    <div className="dbl-event-parameter-panel-parameter-select">
      <NewRegionSelect
        className="dbl-event-parameter-panel-cascader"
        boxClassName="dbl-event-parameter-panel-cascader-box"
        value={value}
        onChange={handleRegionChange}
        onCloseReginSelect={handleRegionChange}
        locale={locale}
        options={regionSelectOptions}
        radioOptions={[]}
        realRegionsList={realRegionsList}
        closeOnScroll={false}
        shortcutOptions={[]}
        popularList={[]}
        selectFilterType="event_parameter"
        language={language}
        placeholder={placeholder}
        firstLevelTitle={getLocalText('overall')}
        renderRightContent={renderRightContent}
        rightListWidth={359}
        button={button}
        {...(rest || {})}
      />
    </div>
  );
};

export default EventParameterPanel;
